import { z } from "zod";

export const identifierSchema = z.string().uuid();

export const tenantStatusSchema = z.enum(["ACTIVE", "SUSPENDED", "CLOSED"]);
export type TenantStatus = z.infer<typeof tenantStatusSchema>;

export const branchStatusSchema = z.enum(["ACTIVE", "INACTIVE"]);
export type BranchStatus = z.infer<typeof branchStatusSchema>;

export const userStatusSchema = z.enum(["ACTIVE", "INACTIVE", "LOCKED"]);
export type UserStatus = z.infer<typeof userStatusSchema>;

export const roleCodeSchema = z.enum([
  "ASESOR",
  "OPTOMETRISTA",
  "GERENTE",
  "SUPERVISOR",
  "AUDITOR",
  "ADMINISTRATIVO",
  "ADMINISTRADOR",
]);
export type RoleCode = z.infer<typeof roleCodeSchema>;

export const permissionCodeSchema = z.enum([
  "CLINICAL_READ",
  "CLINICAL_WRITE",
  "BILLING_READ",
  "BILLING_WRITE",
  "SALES_READ",
  "SALES_WRITE",
  "INVENTORY_READ",
  "INVENTORY_WRITE",
  "LAB_READ",
  "LAB_WRITE",
  "KPI_READ",
  "KPI_WRITE",
  "ADMIN_READ",
  "ADMIN_WRITE",
  "CANCELLATION_REQUEST",
  "CANCELLATION_APPROVE",
  "CANCELLATION_EXECUTE",
]);
export type PermissionCode = z.infer<typeof permissionCodeSchema>;

export const branchSummarySchema = z.object({
  id: identifierSchema,
  code: z.string().min(1).max(40),
  name: z.string().min(1).max(160),
});
export type BranchSummary = z.infer<typeof branchSummarySchema>;

export const sessionIdentitySchema = z.object({
  userId: identifierSchema,
  tenantId: identifierSchema,
  branchId: identifierSchema.nullable(),
  username: z.string().min(1).max(80),
  displayName: z.string().min(1).max(160),
  roles: z.array(roleCodeSchema),
  permissions: z.array(permissionCodeSchema),
  branches: z.array(branchSummarySchema),
  mustChangePassword: z.boolean(),
  expiresAt: z.string().datetime({ offset: true }),
});
export type SessionIdentity = z.infer<typeof sessionIdentitySchema>;

export function getCancellationAccess(identity: SessionIdentity) {
  const canRead = !identity.mustChangePassword && identity.branchId !== null
    && identity.roles.some((role) => ["GERENTE", "SUPERVISOR", "ADMINISTRADOR"].includes(role))
    && identity.permissions.some((permission) =>
      ["CANCELLATION_REQUEST", "CANCELLATION_APPROVE", "CANCELLATION_EXECUTE"].includes(permission));
  const operator = identity.roles.some((role) => ["GERENTE", "ADMINISTRADOR"].includes(role));
  return {
    canRead,
    canRequest: canRead && operator && identity.permissions.includes("CANCELLATION_REQUEST"),
    canExecute: canRead && operator && identity.permissions.includes("CANCELLATION_EXECUTE"),
    canApprove: canRead && identity.roles.some((role) => ["SUPERVISOR", "ADMINISTRADOR"].includes(role))
      && identity.permissions.includes("CANCELLATION_APPROVE"),
  };
}

/** Lookup key only: keep the stored sale folio unchanged in documents and audits. */
export function normalizeSaleFolio(folio: string): string {
  return folio.trim().toUpperCase();
}

export const loginRequestSchema = z.object({
  tenantSlug: z
    .string()
    .trim()
    .min(2)
    .max(80)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
  username: z.string().trim().min(1).max(80),
  password: z.string().min(10).max(256),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const loginResponseSchema = z.object({
  data: sessionIdentitySchema,
});
export type LoginResponse = z.infer<typeof loginResponseSchema>;

export const passwordSchema = z
  .string()
  .min(10)
  .max(256)
  .refine((value) => /[A-ZÁÉÍÓÚÑ]/u.test(value), "Falta una mayúscula.")
  .refine((value) => /[a-záéíóúñ]/u.test(value), "Falta una minúscula.")
  .refine((value) => /\d/u.test(value), "Falta un número.")
  .refine(
    (value) => /[^\p{L}\p{N}\s]/u.test(value),
    "Falta un símbolo.",
  );

export const changePasswordRequestSchema = z.object({
  currentPassword: z.string().min(10).max(256),
  newPassword: passwordSchema,
});
export type ChangePasswordRequest = z.infer<
  typeof changePasswordRequestSchema
>;

export const selectBranchRequestSchema = z.object({
  branchId: identifierSchema,
});
export type SelectBranchRequest = z.infer<typeof selectBranchRequestSchema>;

const usernameSchema = z
  .string()
  .trim()
  .min(3)
  .max(80)
  .regex(
    /^[a-z0-9]+(?:[._-][a-z0-9]+)*$/,
    "Usa minúsculas, números, puntos, guiones o guion bajo.",
  );

const roleCodesSchema = z
  .array(roleCodeSchema)
  .min(1)
  .max(7)
  .refine((value) => new Set(value).size === value.length, {
    message: "No repitas roles.",
  });

const branchIdsSchema = z
  .array(identifierSchema)
  .min(1)
  .max(100)
  .refine((value) => new Set(value).size === value.length, {
    message: "No repitas sucursales.",
  });

export const adminUserInputSchema = z.object({
  username: usernameSchema,
  displayName: z.string().trim().min(3).max(160),
  employeeNumber: z.string().trim().max(40).nullable(),
  email: z.string().trim().email().max(254).nullable(),
  status: z.enum(["ACTIVE", "INACTIVE"]),
  roleCodes: roleCodesSchema,
  branchIds: branchIdsSchema,
});
export type AdminUserInput = z.infer<typeof adminUserInputSchema>;

export const adminUserRecordSchema = z.object({
  id: identifierSchema,
  username: z.string().min(1).max(80),
  displayName: z.string().min(1).max(160),
  employeeNumber: z.string().nullable(),
  email: z.string().nullable(),
  status: userStatusSchema,
  mustChangePassword: z.boolean(),
  roleCodes: z.array(roleCodeSchema),
  branchIds: z.array(identifierSchema),
  failedAttempts: z.number().int().min(0),
  lockedUntil: z.string().datetime({ offset: true }).nullable(),
  lastLoginAt: z.string().datetime({ offset: true }).nullable(),
  createdAt: z.string().datetime({ offset: true }),
  isCurrentUser: z.boolean(),
});
export type AdminUserRecord = z.infer<typeof adminUserRecordSchema>;

export const adminUsersResponseSchema = z.object({
  data: z.array(adminUserRecordSchema),
});
export type AdminUsersResponse = z.infer<typeof adminUsersResponseSchema>;

export const adminOptionsResponseSchema = z.object({
  data: z.object({
    branches: z.array(branchSummarySchema),
    roles: z.array(
      z.object({
        code: roleCodeSchema,
        name: z.string().min(1).max(120),
        description: z.string().min(1).max(500),
      }),
    ),
  }),
});
export type AdminOptionsResponse = z.infer<
  typeof adminOptionsResponseSchema
>;

export const adminUserMutationResponseSchema = z.object({
  data: z.object({
    user: adminUserRecordSchema,
    temporaryPassword: z.string().min(10).max(256).nullable(),
  }),
});
export type AdminUserMutationResponse = z.infer<
  typeof adminUserMutationResponseSchema
>;

export const actionResponseSchema = z.object({
  data: z.object({ ok: z.literal(true) }),
});
export type ActionResponse = z.infer<typeof actionResponseSchema>;

export const inventoryCategoryCodeSchema = z.enum([
  "Sol G",
  "Sol",
  "Sol K",
  "OFT M",
  "OFT H",
  "OFT K",
  "OFT U",
  "LC",
  "ACC",
]);
export type InventoryCategoryCode = z.infer<
  typeof inventoryCategoryCodeSchema
>;

export const catalogProductStatusSchema = z.enum(["ACTIVE", "INACTIVE"]);
export type CatalogProductStatus = z.infer<
  typeof catalogProductStatusSchema
>;

const optionalMeasureSchema = z.number().finite().nullable();

export const catalogProductCreateSchema = z
  .object({
    partNumber: z.string().trim().min(1).max(100),
    categoryCode: inventoryCategoryCodeSchema,
    brand: z.string().trim().min(1).max(100),
    material: z.string().trim().min(1).max(100),
    color: z.string().trim().min(1).max(100),
    measureA: optionalMeasureSchema,
    measureB: optionalMeasureSchema,
    measureDbl: optionalMeasureSchema,
    measureEd: optionalMeasureSchema,
    baseCurve: optionalMeasureSchema,
    acquisitionCost: z.number().finite().positive().max(1_000_000),
    multiplier: z.number().finite().positive().max(100),
    manualPublicPrice: z.number().finite().positive().max(1_000_000).nullable(),
  })
  .superRefine((value, context) => {
    const gradable = ["Sol G", "OFT M", "OFT H", "OFT K", "OFT U"].includes(
      value.categoryCode,
    );
    const ranges = [
      ["measureA", value.measureA, 38, 71],
      ["measureB", value.measureB, 22, 51],
      ["measureDbl", value.measureDbl, 10, 21],
      ["measureEd", value.measureEd, 39, 71],
    ] as const;
    for (const [field, measure, minimum, maximum] of ranges) {
      if (gradable && (measure === null || measure < minimum || measure > maximum)) {
        context.addIssue({
          code: "custom",
          path: [field],
          message: `La medida debe estar entre ${minimum} y ${maximum} mm.`,
        });
      }
      if (!gradable && measure !== null) {
        context.addIssue({ code: "custom", path: [field], message: "Esta categoría no utiliza medidas de armazón." });
      }
    }
    if (value.categoryCode === "Sol G") {
      if (value.baseCurve === null || value.baseCurve < 1 || value.baseCurve > 6) {
        context.addIssue({ code: "custom", path: ["baseCurve"], message: "La curva base debe estar entre 1 y 6." });
      }
    } else if (value.baseCurve !== null) {
      context.addIssue({ code: "custom", path: ["baseCurve"], message: "Esta categoría no utiliza curva base." });
    }
  });
export type CatalogProductCreate = z.infer<typeof catalogProductCreateSchema>;

export const catalogPriceUpdateSchema = z.object({
  acquisitionCost: z.number().finite().positive().max(1_000_000),
  multiplier: z.number().finite().positive().max(100),
  manualPublicPrice: z.number().finite().positive().max(1_000_000).nullable(),
  status: catalogProductStatusSchema,
});
export type CatalogPriceUpdate = z.infer<typeof catalogPriceUpdateSchema>;

export const inventoryCategorySchema = z.object({
  code: inventoryCategoryCodeSchema,
  labelCode: z.string().min(1).max(20),
  name: z.string().min(1).max(160),
  gradable: z.boolean(),
  sortOrder: z.number().int().positive(),
});
export type InventoryCategory = z.infer<typeof inventoryCategorySchema>;

export const catalogProductSchema = z.object({
  id: identifierSchema,
  sku: z.string().regex(/^\d{6}$/),
  partNumber: z.string().min(1).max(100),
  categoryCode: inventoryCategoryCodeSchema,
  categoryLabel: z.string().min(1).max(20),
  categoryName: z.string().min(1).max(160),
  brand: z.string().min(1).max(100),
  material: z.string().min(1).max(100),
  color: z.string().min(1).max(100),
  description: z.string().min(1).max(500),
  gradable: z.boolean(),
  measureA: z.number().nullable(),
  measureB: z.number().nullable(),
  measureDbl: z.number().nullable(),
  measureEd: z.number().nullable(),
  baseCurve: z.number().nullable(),
  acquisitionCost: z.number().positive(),
  multiplier: z.number().positive(),
  calculatedPrice: z.number().positive(),
  manualPublicPrice: z.number().positive().nullable(),
  publicPrice: z.number().positive(),
  status: catalogProductStatusSchema,
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});
export type CatalogProduct = z.infer<typeof catalogProductSchema>;

export const inventoryCategoriesResponseSchema = z.object({
  data: z.array(inventoryCategorySchema),
});
export const catalogProductsResponseSchema = z.object({
  data: z.array(catalogProductSchema),
});
export const catalogProductResponseSchema = z.object({
  data: catalogProductSchema,
});
export const catalogProductResolutionResponseSchema = z.object({
  data: catalogProductSchema.nullable(),
});

export const remittanceStatusSchema = z.enum(["PENDING", "AUTHORIZED"]);
export type RemittanceStatus = z.infer<typeof remittanceStatusSchema>;

export const remittanceItemInputSchema = catalogProductCreateSchema.and(
  z.object({ quantity: z.number().int().min(1).max(9_999) }),
);
export type RemittanceItemInput = z.infer<typeof remittanceItemInputSchema>;

export const remittanceCreateSchema = z.object({
  items: z.array(remittanceItemInputSchema).min(1).max(250),
});
export type RemittanceCreate = z.infer<typeof remittanceCreateSchema>;

export const remittanceAuthorizationSchema = z.object({
  signature: z.string().trim().min(3).max(160),
  authorizationCode: z.string().trim().regex(/^[A-Za-z0-9-]{4,40}$/),
});
export type RemittanceAuthorization = z.infer<typeof remittanceAuthorizationSchema>;

export const inventoryRemittanceItemSchema = z.object({
  id: identifierSchema,
  productId: identifierSchema,
  sku: z.string().regex(/^\d{6}$/),
  partNumber: z.string().min(1).max(100),
  categoryCode: inventoryCategoryCodeSchema,
  brand: z.string().min(1).max(100),
  material: z.string().min(1).max(100),
  color: z.string().min(1).max(100),
  quantity: z.number().int().min(1),
  acquisitionCost: z.number().positive(),
  multiplier: z.number().positive(),
  publicPrice: z.number().positive(),
  lineAcquisitionTotal: z.number().positive(),
  lineSaleTotal: z.number().positive(),
});
export type InventoryRemittanceItem = z.infer<typeof inventoryRemittanceItemSchema>;

export const inventoryRemittanceSchema = z.object({
  id: identifierSchema,
  folio: z.string().min(1).max(40),
  branchId: identifierSchema,
  branchCode: z.string().min(1).max(40),
  branchName: z.string().min(1).max(160),
  status: remittanceStatusSchema,
  productCount: z.number().int().min(1),
  pieceCount: z.number().int().min(1),
  acquisitionTotal: z.number().positive(),
  saleTotal: z.number().positive(),
  createdByName: z.string().min(1).max(160),
  createdAt: z.string().datetime({ offset: true }),
  authorizedByName: z.string().min(1).max(160).nullable(),
  authorizationSignature: z.string().min(1).max(160).nullable(),
  authorizedAt: z.string().datetime({ offset: true }).nullable(),
  items: z.array(inventoryRemittanceItemSchema),
});
export type InventoryRemittance = z.infer<typeof inventoryRemittanceSchema>;

export const inventoryRemittancesResponseSchema = z.object({ data: z.array(inventoryRemittanceSchema) });
export const inventoryRemittanceResponseSchema = z.object({ data: inventoryRemittanceSchema });

export const branchStockItemSchema = z.object({
  productId: identifierSchema,
  sku: z.string().regex(/^\d{6}$/),
  partNumber: z.string().min(1).max(100),
  categoryCode: inventoryCategoryCodeSchema,
  categoryLabel: z.string().min(1).max(20),
  brand: z.string().min(1).max(100),
  material: z.string().min(1).max(100),
  color: z.string().min(1).max(100),
  publicPrice: z.number().positive(),
  quantity: z.number().int().min(0),
  updatedAt: z.string().datetime({ offset: true }),
});
export type BranchStockItem = z.infer<typeof branchStockItemSchema>;
export const branchStockResponseSchema = z.object({ data: z.array(branchStockItemSchema) });

export const inventoryMovementTypeSchema = z.enum([
  "ENTRADA_REMESA",
  "AJUSTE_CONTEO",
  "SALIDA_VENTA_MOSTRADOR",
]);
export type InventoryMovementType = z.infer<typeof inventoryMovementTypeSchema>;

export const inventoryMovementSchema = z.object({
  id: identifierSchema,
  folio: z.string().min(1).max(80),
  movementType: inventoryMovementTypeSchema,
  sku: z.string().regex(/^\d{6}$/),
  partNumber: z.string().min(1).max(100),
  quantity: z.number().int().refine((value) => value !== 0),
  stockBefore: z.number().int().min(0),
  stockAfter: z.number().int().min(0),
  actorName: z.string().min(1).max(160),
  occurredAt: z.string().datetime({ offset: true }),
});
export type InventoryMovement = z.infer<typeof inventoryMovementSchema>;
export const inventoryMovementsResponseSchema = z.object({ data: z.array(inventoryMovementSchema) });

export const inventoryCountStatusSchema = z.enum(["PENDING", "AUTHORIZED"]);
export type InventoryCountStatus = z.infer<typeof inventoryCountStatusSchema>;

export const inventoryCountItemInputSchema = z.object({
  productId: identifierSchema,
  countedQuantity: z.number().int().min(0).max(999_999),
});
export type InventoryCountItemInput = z.infer<typeof inventoryCountItemInputSchema>;

export const inventoryCountCreateSchema = z.object({
  items: z.array(inventoryCountItemInputSchema).min(1).max(500),
}).superRefine((value, context) => {
  const seen = new Set<string>();
  value.items.forEach((item, index) => {
    if (seen.has(item.productId)) {
      context.addIssue({ code: "custom", path: ["items", index, "productId"], message: "No repitas productos en el conteo." });
    }
    seen.add(item.productId);
  });
});
export type InventoryCountCreate = z.infer<typeof inventoryCountCreateSchema>;

export const inventoryCountAuthorizationSchema = remittanceAuthorizationSchema;
export type InventoryCountAuthorization = z.infer<typeof inventoryCountAuthorizationSchema>;

export const inventoryCountItemSchema = z.object({
  id: identifierSchema,
  productId: identifierSchema,
  sku: z.string().regex(/^\d{6}$/),
  partNumber: z.string().min(1).max(100),
  categoryLabel: z.string().min(1).max(20),
  brand: z.string().min(1).max(100),
  color: z.string().min(1).max(100),
  expectedQuantity: z.number().int().min(0),
  countedQuantity: z.number().int().min(0),
  difference: z.number().int(),
});
export type InventoryCountItem = z.infer<typeof inventoryCountItemSchema>;

export const inventoryCountSchema = z.object({
  id: identifierSchema,
  folio: z.string().min(1).max(40),
  branchId: identifierSchema,
  branchCode: z.string().min(1).max(40),
  branchName: z.string().min(1).max(160),
  status: inventoryCountStatusSchema,
  lineCount: z.number().int().min(1),
  expectedTotal: z.number().int().min(0),
  countedTotal: z.number().int().min(0),
  differenceTotal: z.number().int(),
  createdById: identifierSchema,
  createdByName: z.string().min(1).max(160),
  createdAt: z.string().datetime({ offset: true }),
  authorizedByName: z.string().min(1).max(160).nullable(),
  authorizationSignature: z.string().min(1).max(160).nullable(),
  authorizedAt: z.string().datetime({ offset: true }).nullable(),
  items: z.array(inventoryCountItemSchema),
});
export type InventoryCount = z.infer<typeof inventoryCountSchema>;
export const inventoryCountsResponseSchema = z.object({ data: z.array(inventoryCountSchema) });
export const inventoryCountResponseSchema = z.object({ data: inventoryCountSchema });

export const lensPriceComponentTypeSchema = z.enum([
  "BASE",
  "AR",
  "FOTOSENSIBLE",
  "UV_COLOR",
  "RANGO_RX",
]);
export type LensPriceComponentType = z.infer<typeof lensPriceComponentTypeSchema>;

export const lensPriceStatusSchema = z.enum(["ACTIVE", "INACTIVE"]);
export type LensPriceStatus = z.infer<typeof lensPriceStatusSchema>;

export const lensTypeSchema = z.enum(["Visión sencilla", "Progresivo", "Bifocal"]);
export type LensType = z.infer<typeof lensTypeSchema>;
export const lensDesignSchema = z.enum([
  "Convencional",
  "Digital / HD",
  "Asférico Digital",
  "Prog. Daily",
  "Prog. HD Vision",
  "Prog. Smart Vision",
  "Prog. IA Performance",
  "Prog. IA Driver EXP",
  "Bifocal",
  "Bifocal HD Dual visión",
]);
export type LensDesign = z.infer<typeof lensDesignSchema>;
export const lensMaterialSchema = z.enum([
  "CR-39",
  "Policarbonato",
  "Alto índice",
  "1.74",
  "Polarizado Gris",
  "Polarizado Café",
]);
export type LensMaterial = z.infer<typeof lensMaterialSchema>;

export const uvOperationalColors = [
  "Gris", "Café", "Amarillo", "Ámbar", "Morado", "Azul", "Verde", "Igualado",
] as const;
export const mirrorOperationalColors = ["Azul", "Dorado", "Plata", "Verde"] as const;
export const lensTintLevels = ["Nivel 0.5", "Nivel 1", "Nivel 2", "Nivel 3"] as const;
export const lensOperationalColorSchema = z.enum([
  ...uvOperationalColors,
  ...mirrorOperationalColors,
]);
export const lensTintLevelSchema = z.enum(lensTintLevels);
export const lensOperationalSpecificationsSchema = z.object({
  uvColor: lensOperationalColorSchema.nullable(),
  tintLevel: lensTintLevelSchema.nullable(),
});
export type LensOperationalSpecifications = z.infer<typeof lensOperationalSpecificationsSchema>;

export type ProgressiveRxRange = {
  sphereMin: number;
  sphereMax: number;
  cylinderMin: number;
  cylinderMax: number;
};

export type ProgressiveSalesRule = {
  materials: readonly LensMaterial[];
  treatments: readonly string[];
  photosensitive: Readonly<Record<string, readonly string[]>>;
  uvColor: readonly string[];
  corridors: readonly number[];
  minHeight: number | null;
  minAdd: number;
  maxAdd: number;
  maxCombined: number | null;
  cr39: ProgressiveRxRange | null;
  polycarbonate: ProgressiveRxRange | null;
};

/**
 * Reglas comerciales y de fabricación vigentes heredadas del sistema operativo.
 * Son compartidas por la interfaz y la API para que ambos lados validen lo mismo.
 */
export const progressiveSalesRules: Partial<Record<LensDesign, ProgressiveSalesRule>> = {
  "Prog. Daily": {
    materials: ["Policarbonato", "CR-39", "Alto índice", "Polarizado Gris", "Polarizado Café"],
    treatments: ["", "AR Green", "AR Blue", "Ninguno"],
    photosensitive: { "Fotosensible inicial": ["Gris", "Café"] },
    uvColor: ["", "Color UV"],
    corridors: [20],
    minHeight: null,
    minAdd: 1,
    maxAdd: 3,
    maxCombined: null,
    cr39: null,
    polycarbonate: null,
  },
  "Prog. HD Vision": {
    materials: ["Policarbonato", "CR-39", "Alto índice", "Polarizado Gris", "Polarizado Café"],
    treatments: ["", "AR Green Plus", "AR Blue Premium", "AR Cosmo UltraClear", "Ninguno"],
    photosensitive: {
      "Fotosensible inicial": ["Gris", "Café"],
      "Fotosensible Transitions": ["Gris", "Café"],
      Drivewear: ["COLOR DRIVEWEAR"],
    },
    uvColor: ["", "Color UV", "Espejeado"],
    corridors: [16, 19],
    minHeight: 16,
    minAdd: 1,
    maxAdd: 3,
    maxCombined: 9,
    cr39: { sphereMin: -6, sphereMax: 6, cylinderMin: -4, cylinderMax: 0 },
    polycarbonate: { sphereMin: -8, sphereMax: 6, cylinderMin: -5, cylinderMax: 0 },
  },
  "Prog. Smart Vision": {
    materials: ["Policarbonato", "CR-39", "Polarizado Gris", "Polarizado Café"],
    treatments: ["", "AR Green Plus", "AR Blue Premium", "AR Cosmo UltraClear", "Ninguno"],
    photosensitive: {
      "Fotosensible inicial": ["Gris", "Café"],
      "Fotosensible Transitions": ["Gris", "Café", "Rubí", "Verde RB", "Zafiro", "Esmeralda", "Ámbar"],
      Drivewear: ["COLOR DRIVEWEAR"],
    },
    uvColor: ["", "Color UV", "Espejeado"],
    corridors: [15, 18],
    minHeight: 15,
    minAdd: 0.75,
    maxAdd: 3.5,
    maxCombined: 12,
    cr39: { sphereMin: -6, sphereMax: 6, cylinderMin: -4, cylinderMax: 0 },
    polycarbonate: { sphereMin: -8, sphereMax: 6, cylinderMin: -5, cylinderMax: 0 },
  },
  "Prog. IA Performance": {
    materials: ["Policarbonato", "CR-39", "1.74", "Polarizado Gris", "Polarizado Café"],
    treatments: ["", "AR Green Plus", "AR Blue Premium", "AR Cosmo UltraClear", "Ninguno"],
    photosensitive: {
      "Fotosensible inicial": ["Gris", "Café"],
      "Fotosensible Transitions": ["Gris", "Café", "Rubí", "Verde RB", "Zafiro", "Esmeralda", "Ámbar"],
      Drivewear: ["COLOR DRIVEWEAR"],
    },
    uvColor: ["", "Color UV", "Espejeado"],
    corridors: [11],
    minHeight: 11,
    minAdd: 0.75,
    maxAdd: 4,
    maxCombined: 12,
    cr39: { sphereMin: -6, sphereMax: 6, cylinderMin: -4, cylinderMax: 0 },
    polycarbonate: { sphereMin: -8, sphereMax: 6, cylinderMin: -6, cylinderMax: 0 },
  },
  "Prog. IA Driver EXP": {
    materials: ["Policarbonato", "Polarizado Gris", "Polarizado Café"],
    treatments: ["AR Cosmo UltraClear"],
    photosensitive: {
      "Fotosensible inicial": ["Gris", "Café"],
      "Fotosensible Transitions": ["Gris", "Café", "Rubí", "Verde RB", "Zafiro", "Esmeralda", "Ámbar"],
    },
    uvColor: [""],
    corridors: [18],
    minHeight: 18,
    minAdd: 0.75,
    maxAdd: 3.5,
    maxCombined: 12,
    cr39: null,
    polycarbonate: { sphereMin: -8, sphereMax: 6, cylinderMin: -6, cylinderMax: 0 },
  },
};

const nullableCatalogText = z.string().trim().min(1).max(160).nullable();

export const lensPriceComponentInputSchema = z
  .object({
    componentType: lensPriceComponentTypeSchema,
    lensType: lensTypeSchema.nullable(),
    design: lensDesignSchema.nullable(),
    material: lensMaterialSchema.nullable(),
    optionName: nullableCatalogText,
    acquisitionCost: z.number().finite().positive().max(1_000_000).nullable(),
    salePrice: z.number().finite().positive().max(1_000_000).nullable(),
  })
  .superRefine((value, context) => {
    if (value.componentType === "BASE") {
      if (!value.lensType) context.addIssue({ code: "custom", path: ["lensType"], message: "Selecciona el tipo de lente." });
      if (!value.design) context.addIssue({ code: "custom", path: ["design"], message: "Selecciona el diseño." });
      if (!value.material) context.addIssue({ code: "custom", path: ["material"], message: "Selecciona el material." });
      if (value.optionName !== null) context.addIssue({ code: "custom", path: ["optionName"], message: "La base no utiliza nombre adicional." });
    } else {
      if (value.lensType !== null || value.design !== null || value.material !== null) {
        context.addIssue({ code: "custom", path: ["componentType"], message: "Este componente no utiliza tipo, diseño ni material." });
      }
      if (value.componentType === "RANGO_RX" && value.optionName !== null) {
        context.addIssue({ code: "custom", path: ["optionName"], message: "Rango RX es un cargo general." });
      }
      if (value.componentType !== "RANGO_RX" && !value.optionName) {
        context.addIssue({ code: "custom", path: ["optionName"], message: "Selecciona el concepto." });
      }
    }
  });
export type LensPriceComponentInput = z.infer<typeof lensPriceComponentInputSchema>;

export const lensPriceComponentUpdateSchema = z.object({
  acquisitionCost: z.number().finite().positive().max(1_000_000).nullable(),
  salePrice: z.number().finite().positive().max(1_000_000).nullable(),
  status: lensPriceStatusSchema,
  reason: z.string().trim().min(5).max(500),
});
export type LensPriceComponentUpdate = z.infer<typeof lensPriceComponentUpdateSchema>;

export const lensPriceComponentSchema = z.object({
  id: identifierSchema,
  priceCode: z.string().min(1).max(80),
  componentType: lensPriceComponentTypeSchema,
  lensType: lensTypeSchema.nullable(),
  design: lensDesignSchema.nullable(),
  material: lensMaterialSchema.nullable(),
  optionName: nullableCatalogText,
  label: z.string().min(1).max(500),
  acquisitionCost: z.number().positive().nullable(),
  salePrice: z.number().positive().nullable(),
  readyForSale: z.boolean(),
  status: lensPriceStatusSchema,
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});
export type LensPriceComponent = z.infer<typeof lensPriceComponentSchema>;

export const lensPriceOptionsSchema = z.object({
  lensTypes: z.array(lensTypeSchema),
  designsByType: z.record(lensTypeSchema, z.array(lensDesignSchema)),
  materials: z.array(lensMaterialSchema),
  antireflective: z.array(z.string().min(1)),
  photosensitive: z.array(z.string().min(1)),
  uvColor: z.array(z.string().min(1)),
  photoColors: z.array(z.string().min(1)),
  tintLevels: z.array(lensTintLevelSchema),
  tintTypes: z.array(z.string().min(1)),
});
export type LensPriceOptions = z.infer<typeof lensPriceOptionsSchema>;

export const lensPriceCatalogResponseSchema = z.object({
  data: z.object({
    options: lensPriceOptionsSchema,
    prices: z.array(lensPriceComponentSchema),
  }),
});
export const lensPriceComponentResponseSchema = z.object({ data: lensPriceComponentSchema });

export const lensPriceCombinationInputSchema = z
  .object({
    basePriceId: identifierSchema,
    arPriceId: identifierSchema.nullable(),
    photoPriceId: identifierSchema.nullable(),
    photoColor: z.string().trim().min(1).max(120).nullable(),
    uvPriceId: identifierSchema.nullable(),
    appliesRangeRx: z.boolean(),
    rangePriceId: identifierSchema.nullable(),
    preferredSupplier: z.string().trim().min(1).max(160).nullable(),
    alternateSuppliers: z.array(z.string().trim().min(1).max(160)).max(5),
  })
  .superRefine((value, context) => {
    if ((value.photoPriceId === null) !== (value.photoColor === null)) {
      context.addIssue({
        code: "custom",
        path: ["photoColor"],
        message: "Selecciona el fotosensible y su color juntos.",
      });
    }
    if (value.appliesRangeRx !== (value.rangePriceId !== null)) {
      context.addIssue({
        code: "custom",
        path: ["rangePriceId"],
        message: "Selecciona el cargo de rango RX cuando corresponda.",
      });
    }
  });
export type LensPriceCombinationInput = z.infer<typeof lensPriceCombinationInputSchema>;

export const lensPriceCombinationComponentSchema = z.object({
  componentType: lensPriceComponentTypeSchema,
  priceCode: z.string().min(1).max(80),
  label: z.string().min(1).max(500),
  salePrice: z.number().positive().nullable(),
  readyForSale: z.boolean(),
});
export type LensPriceCombinationComponent = z.infer<typeof lensPriceCombinationComponentSchema>;

export const lensPriceCombinationSchema = z.object({
  id: identifierSchema,
  combinationCode: z.string().min(1).max(80),
  basePriceId: identifierSchema,
  arPriceId: identifierSchema.nullable(),
  photoPriceId: identifierSchema.nullable(),
  photoColor: z.string().nullable(),
  uvPriceId: identifierSchema.nullable(),
  appliesRangeRx: z.boolean(),
  rangePriceId: identifierSchema.nullable(),
  preferredSupplier: z.string().nullable(),
  alternateSuppliers: z.array(z.string()),
  lensType: lensTypeSchema,
  design: lensDesignSchema,
  material: lensMaterialSchema,
  baseLabel: z.string(),
  arLabel: z.string().nullable(),
  photoLabel: z.string().nullable(),
  uvLabel: z.string().nullable(),
  priceBreakdown: z.array(lensPriceCombinationComponentSchema).min(1).max(20),
  missingPriceComponents: z.array(z.string().min(1).max(600)).max(5),
  totalPrice: z.number().positive().nullable(),
  readyForSale: z.boolean(),
  status: lensPriceStatusSchema,
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});
export type LensPriceCombination = z.infer<typeof lensPriceCombinationSchema>;

export const lensPriceCombinationsResponseSchema = z.object({ data: z.array(lensPriceCombinationSchema) });
export const lensPriceCombinationResponseSchema = z.object({ data: lensPriceCombinationSchema });

const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Usa una fecha válida.");
const quarterStepSchema = (minimum: number, maximum: number) => z.number()
  .finite()
  .min(minimum)
  .max(maximum)
  .refine((value) => Math.abs(value * 4 - Math.round(value * 4)) < 0.0001, "Usa pasos de 0.25.");

export const customerGenderSchema = z.enum(["Masculino", "Femenino", "Prefiere no responder"]);
export type CustomerGender = z.infer<typeof customerGenderSchema>;

export const customerInputSchema = z.object({
  names: z.string().trim().min(1).max(120),
  paternalSurname: z.string().trim().min(1).max(100),
  maternalSurname: z.string().trim().min(1).max(100),
  birthDate: isoDateSchema,
  gender: customerGenderSchema.nullable(),
  phone: z.string().trim().regex(/^\d{10}$/, "Captura los 10 dígitos del teléfono."),
  email: z.string().trim().email().max(254).nullable(),
  guardianName: z.string().trim().min(3).max(180).nullable().default(null),
  guardianRelationship: z.string().trim().min(2).max(80).nullable().default(null),
  guardianPhone: z.string().trim().regex(/^\d{10}$/, "Captura los 10 dígitos del teléfono del tutor.").nullable().default(null),
  privacyAccepted: z.literal(true),
});
export type CustomerInput = z.infer<typeof customerInputSchema>;

export const customerSearchSchema = z.object({
  names: z.string().trim().max(120).default(""),
  paternalSurname: z.string().trim().max(100).default(""),
  maternalSurname: z.string().trim().max(100).default(""),
  birthDate: z.union([isoDateSchema, z.literal("")]).default(""),
  phone: z.string().trim().max(10).default(""),
}).superRefine((value, context) => {
  const criteria = [value.names, value.paternalSurname, value.maternalSurname, value.birthDate, value.phone]
    .filter((item) => item.length > 0);
  if (criteria.length < 2) {
    context.addIssue({ code: "custom", message: "Captura al menos dos datos para buscar al cliente." });
  }
  if (value.phone && !/^\d{10}$/.test(value.phone)) {
    context.addIssue({ code: "custom", path: ["phone"], message: "El teléfono debe tener 10 dígitos." });
  }
});
export type CustomerSearch = z.infer<typeof customerSearchSchema>;

export const customerSchema = z.object({
  id: identifierSchema,
  names: z.string().min(1).max(120),
  paternalSurname: z.string().min(1).max(100),
  maternalSurname: z.string().min(1).max(100),
  fullName: z.string().min(3).max(330),
  birthDate: isoDateSchema,
  age: z.number().int().min(0).max(130),
  gender: customerGenderSchema.nullable(),
  phone: z.string().regex(/^\d{10}$/),
  email: z.string().email().nullable(),
  guardianName: z.string().min(3).max(180).nullable().default(null),
  guardianRelationship: z.string().min(2).max(80).nullable().default(null),
  guardianPhone: z.string().regex(/^\d{10}$/).nullable().default(null),
  privacyAcceptedAt: z.string().datetime({ offset: true }),
  createdAt: z.string().datetime({ offset: true }),
});
export type Customer = z.infer<typeof customerSchema>;
export const customersResponseSchema = z.object({ data: z.array(customerSchema) });
export const customerResponseSchema = z.object({ data: customerSchema });

export const prescriptionOriginSchema = z.enum(["COSMO", "EXTERNA"]);
export type PrescriptionOrigin = z.infer<typeof prescriptionOriginSchema>;

export const prescriptionEyeInputSchema = z.object({
  sphere: quarterStepSchema(-18, 12),
  cylinder: quarterStepSchema(-7, 0),
  axis: z.number().int().min(0).max(180).nullable(),
  addition: quarterStepSchema(0.5, 4).nullable(),
  dnp: z.number().finite().min(20).max(45),
  baseCurve: z.number().int().min(1).max(9).nullable(),
  height: z.number().finite().min(10).max(45),
  corridor: z.number().int().min(1).max(30).nullable(),
}).superRefine((value, context) => {
  if (Math.abs(value.cylinder) > 0.0001 && value.axis === null) {
    context.addIssue({ code: "custom", path: ["axis"], message: "El eje es obligatorio cuando existe cilindro." });
  }
  if (Math.abs(value.cylinder) <= 0.0001 && value.axis !== null) {
    context.addIssue({ code: "custom", path: ["axis"], message: "El eje no aplica cuando el cilindro es 0.00." });
  }
});
export type PrescriptionEyeInput = z.infer<typeof prescriptionEyeInputSchema>;

export const prismDirectionSchema = z.enum(["IN", "OUT", "UP", "DOWN"]);
export type PrismDirection = z.infer<typeof prismDirectionSchema>;

export const prismMeasurementInputSchema = z.object({
  value: quarterStepSchema(0.25, 20),
  direction: prismDirectionSchema,
});
export type PrismMeasurementInput = z.infer<typeof prismMeasurementInputSchema>;

export const prismEyeInputSchema = z.object({
  primary: prismMeasurementInputSchema.nullable().default(null),
  secondary: prismMeasurementInputSchema.nullable().default(null),
});
export type PrismEyeInput = z.infer<typeof prismEyeInputSchema>;

export const prismDataInputSchema = z.object({
  enabled: z.boolean().default(false),
  od: prismEyeInputSchema.nullable().default(null),
  oi: prismEyeInputSchema.nullable().default(null),
}).superRefine((value, context) => {
  if (value.enabled && (!value.od?.primary || !value.oi?.primary)) {
    context.addIssue({
      code: "custom",
      path: ["enabled"],
      message: "Captura el primer prisma completo para ambos ojos.",
    });
  }
  if (!value.enabled && (value.od !== null || value.oi !== null)) {
    context.addIssue({
      code: "custom",
      path: ["enabled"],
      message: "Activa la captura de prismas antes de registrar valores.",
    });
  }
});
export type PrismDataInput = z.infer<typeof prismDataInputSchema>;

export const opticalPrescriptionInputSchema = z.object({
  origin: prescriptionOriginSchema,
  externalConfirmed: z.boolean(),
  prescriberEmployeeNumber: z.string().trim().min(1).max(50).nullable().default(null),
  od: prescriptionEyeInputSchema,
  oi: prescriptionEyeInputSchema,
  prismData: prismDataInputSchema.default({ enabled: false, od: null, oi: null }),
  rxSatisfied: z.enum(["YES", "NO"]).nullable().default(null),
  rxNotSatisfiedComments: z.string().trim().max(1000).nullable().default(null),
  laboratoryComments: z.string().trim().max(2500).nullable(),
}).superRefine((value, context) => {
  if (value.origin === "EXTERNA" && !value.externalConfirmed) {
    context.addIssue({ code: "custom", path: ["externalConfirmed"], message: "Confirma que la receta externa fue revisada y resguardada." });
  }
  if (value.origin === "COSMO" && value.externalConfirmed) {
    context.addIssue({ code: "custom", path: ["externalConfirmed"], message: "La confirmación solo aplica a recetas externas." });
  }
  if (value.origin === "COSMO" && !value.prescriberEmployeeNumber) {
    context.addIssue({ code: "custom", path: ["prescriberEmployeeNumber"], message: "Selecciona al optometrista que prescribió la receta." });
  }
  if (value.origin === "EXTERNA" && value.prescriberEmployeeNumber) {
    context.addIssue({ code: "custom", path: ["prescriberEmployeeNumber"], message: "El número de empleado sólo aplica a recetas prescritas por Cosmo Ópticas." });
  }
  if (value.rxSatisfied === "NO" && (!value.rxNotSatisfiedComments || value.rxNotSatisfiedComments.length < 3)) {
    context.addIssue({ code: "custom", path: ["rxNotSatisfiedComments"], message: "Describe por qué el cliente no está conforme con la graduación." });
  }
  if (value.rxSatisfied !== "NO" && value.rxNotSatisfiedComments) {
    context.addIssue({ code: "custom", path: ["rxNotSatisfiedComments"], message: "El comentario de inconformidad sólo aplica cuando el cliente responde No." });
  }
});
export type OpticalPrescriptionInput = z.infer<typeof opticalPrescriptionInputSchema>;

export const quoteFrameInputSchema = z.discriminatedUnion("origin", [
  z.object({
    origin: z.literal("CATALOG"),
    productId: identifierSchema,
    brand: z.null(), model: z.null(), material: z.null(), condition: z.null(), comments: z.null(),
  }),
  z.object({
    origin: z.literal("CUSTOMER"),
    productId: z.null(),
    brand: z.string().trim().min(1).max(100),
    model: z.string().trim().min(1).max(100),
    material: z.string().trim().min(1).max(100),
    condition: z.enum(["Nuevo", "Usado"]),
    comments: z.string().trim().max(1000).nullable(),
    measurements: z.object({
      measureA: z.number().finite().min(38).max(71),
      measureB: z.number().finite().min(22).max(51),
      measureDbl: z.number().finite().min(10).max(21),
      measureEd: z.number().finite().min(39).max(71),
    }),
  }),
]);
export type QuoteFrameInput = z.infer<typeof quoteFrameInputSchema>;

export const quoteCreateSchema = z.object({
  customerId: identifierSchema,
  frame: quoteFrameInputSchema,
  bevelType: z.enum(["Biselado completo", "Ranurado", "Tres piezas"]),
  bevelFinish: z.enum(["Mate", "Abrillantado", "Facetado", "Facetado+Abrillantado"]).nullable(),
  lensCombinationId: identifierSchema,
  lensSpecifications: lensOperationalSpecificationsSchema.default({ uvColor: null, tintLevel: null }),
  prescription: opticalPrescriptionInputSchema,
  noPurchaseReason: z.enum(["Compra en quincena", "Necesita autorización", "No tiene presupuesto", "Otro"]),
  noPurchaseComments: z.string().trim().max(1000).nullable(),
  followUpDate: isoDateSchema,
  estimatedDeliveryDate: isoDateSchema,
}).superRefine((value, context) => {
  if (value.bevelType !== "Biselado completo" && !value.bevelFinish) {
    context.addIssue({ code: "custom", path: ["bevelFinish"], message: "Selecciona el acabado del bisel." });
  }
  if (value.noPurchaseReason === "Otro" && (!value.noPurchaseComments || value.noPurchaseComments.length < 3)) {
    context.addIssue({ code: "custom", path: ["noPurchaseComments"], message: "Describe el motivo de no compra." });
  }
  if (value.prescription.rxSatisfied === null) {
    context.addIssue({ code: "custom", path: ["prescription", "rxSatisfied"], message: "Confirma si el cliente está conforme con su graduación." });
  }
});
export type QuoteCreate = z.infer<typeof quoteCreateSchema>;

export const opticalQuoteStatusSchema = z.enum(["ACTIVE", "CONVERTED"]);
export type OpticalQuoteStatus = z.infer<typeof opticalQuoteStatusSchema>;

export const opticalQuoteSchema = z.object({
  id: identifierSchema,
  folio: z.string().min(1).max(80),
  status: opticalQuoteStatusSchema,
  branchId: identifierSchema,
  customer: customerSchema,
  frame: z.object({
    origin: z.enum(["CATALOG", "CUSTOMER"]),
    productId: identifierSchema.nullable(),
    sku: z.string().nullable(),
    partNumber: z.string().nullable().optional(),
    brand: z.string().nullable().optional(),
    material: z.string().nullable().optional(),
    color: z.string().nullable().optional(),
    description: z.string().min(1),
    price: z.number().positive(),
    quantityAvailable: z.number().int().min(0).nullable(),
    measurements: z.object({
      measureA: z.number().min(38).max(71),
      measureB: z.number().min(22).max(51),
      measureDbl: z.number().min(10).max(21),
      measureEd: z.number().min(39).max(71),
      baseCurve: z.number().min(1).max(9).nullable(),
    }).nullable().default(null),
    customerDetails: z.object({
      brand: z.string().min(1).max(100),
      model: z.string().min(1).max(100),
      material: z.string().min(1).max(100),
      condition: z.enum(["Nuevo", "Usado"]),
      comments: z.string().max(1000).nullable(),
    }).nullable().default(null),
  }),
  lens: z.object({
    combinationId: identifierSchema,
    lensType: lensTypeSchema,
    design: lensDesignSchema,
    material: lensMaterialSchema,
    description: z.string().min(1),
    price: z.number().positive(),
    priceBreakdown: z.array(lensPriceCombinationComponentSchema).min(1).max(20).optional(),
    uvColor: lensOperationalColorSchema.nullable().default(null),
    tintLevel: lensTintLevelSchema.nullable().default(null),
  }),
  prescription: opticalPrescriptionInputSchema,
  bevelType: z.string().min(1),
  bevelFinish: z.string().nullable(),
  noPurchaseReason: z.string().min(1),
  noPurchaseComments: z.string().nullable(),
  followUpDate: isoDateSchema,
  validUntil: isoDateSchema,
  estimatedDeliveryDate: isoDateSchema,
  subtotal: z.number().positive(),
  vat: z.number().min(0),
  total: z.number().positive(),
  createdByName: z.string().min(1),
  createdAt: z.string().datetime({ offset: true }),
});
export type OpticalQuote = z.infer<typeof opticalQuoteSchema>;

export const prescriptionHistoryItemSchema = z.object({
  id: identifierSchema,
  prescription: opticalPrescriptionInputSchema,
  prescriberName: z.string().min(1).max(180).nullable(),
  createdAt: z.string().datetime({ offset: true }),
});
export type PrescriptionHistoryItem = z.infer<typeof prescriptionHistoryItemSchema>;

export const prescriptionHistoryResponseSchema = z.object({
  data: z.array(prescriptionHistoryItemSchema),
});

export const salesFrameOptionSchema = branchStockItemSchema.extend({
  description: z.string().min(1).max(500),
  measureA: z.number().min(38).max(71),
  measureB: z.number().min(22).max(51),
  measureDbl: z.number().min(10).max(21),
  measureEd: z.number().min(39).max(71),
  baseCurve: z.number().min(1).max(9).nullable(),
});
export type SalesFrameOption = z.infer<typeof salesFrameOptionSchema>;
export const salesFrameOptionResponseSchema = z.object({ data: salesFrameOptionSchema });

export const salesPrescriberOptionSchema = z.object({
  userId: identifierSchema,
  employeeNumber: z.string().min(1).max(50),
  displayName: z.string().min(1).max(180),
});
export type SalesPrescriberOption = z.infer<typeof salesPrescriberOptionSchema>;

export const salesQuoteOptionsResponseSchema = z.object({
  data: z.object({
    customerFrameServicePrice: z.number().positive(),
    stock: z.array(salesFrameOptionSchema),
    combinations: z.array(lensPriceCombinationSchema),
    prescribers: z.array(salesPrescriberOptionSchema),
  }),
});
export type SalesQuoteOptionsResponse = z.infer<typeof salesQuoteOptionsResponseSchema>;
export const opticalQuotesResponseSchema = z.object({ data: z.array(opticalQuoteSchema) });
export const opticalQuoteResponseSchema = z.object({ data: opticalQuoteSchema });

export const counterSaleProductSchema = z.object({
  productId: identifierSchema,
  sku: z.string().regex(/^\d{6}$/),
  categoryCode: z.enum(["ACC", "Sol", "Sol K", "Sol G"]),
  categoryLabel: z.string().min(1).max(20),
  description: z.string().min(1).max(500),
  brand: z.string().min(1).max(100),
  color: z.string().min(1).max(100),
  publicPrice: z.number().positive(),
  physicalQuantity: z.number().int().min(0),
  reservedQuantity: z.number().int().min(0),
  availableQuantity: z.number().int().min(0),
});
export type CounterSaleProduct = z.infer<typeof counterSaleProductSchema>;
export const counterSaleProductResponseSchema = z.object({ data: counterSaleProductSchema });

export const counterSaleOperationSchema = z.enum(["LIQUIDATED", "LAYAWAY_50"]);
export type CounterSaleOperation = z.infer<typeof counterSaleOperationSchema>;

export const counterPaymentMethodSchema = z.enum(["01", "02", "03", "04", "28", "99"]);
export type CounterPaymentMethod = z.infer<typeof counterPaymentMethodSchema>;

export const standardPaymentMethodSchema = z.enum(["01", "02", "03", "04", "28"]);
export type StandardPaymentMethod = z.infer<typeof standardPaymentMethodSchema>;

export const counterSalePaymentInputSchema = z.object({
  methodCode: standardPaymentMethodSchema,
  reference: z.string().trim().max(120).nullable(),
  amount: z.number().finite().positive().max(99_999_999.99),
}).superRefine((value, context) => {
  if (value.methodCode !== "01" && !value.reference) {
    context.addIssue({
      code: "custom",
      path: ["reference"],
      message: "La referencia es obligatoria cuando el pago no es en efectivo.",
    });
  }
});
export type CounterSalePaymentInput = z.infer<typeof counterSalePaymentInputSchema>;

export const protectedSalePaymentInputSchema = z.discriminatedUnion("kind", [
  counterSalePaymentInputSchema.extend({ kind: z.literal("STANDARD") }),
  z.object({
    kind: z.literal("INTERNAL_CREDIT_NOTE"),
    redemptionCode: z.string().trim().toUpperCase().regex(/^[A-Z2-9]{10}$/),
    amount: z.number().finite().positive().max(99_999_999.99),
  }),
]);
export type ProtectedSalePaymentInput = z.infer<typeof protectedSalePaymentInputSchema>;

export const saleInvoiceDecisionSchema = z.enum(["NOT_APPLICABLE", "NO", "YES", "ALREADY_REQUESTED"]);
export type SaleInvoiceDecision = z.infer<typeof saleInvoiceDecisionSchema>;

export const counterSaleCustomerInputSchema = customerInputSchema.omit({ gender: true });
export type CounterSaleCustomerInput = z.infer<typeof counterSaleCustomerInputSchema>;

export const fiscalRegimeSchema = z.enum([
  "601", "603", "605", "606", "607", "608", "610", "611", "612",
  "614", "615", "616", "620", "621", "622", "623", "624", "625", "626",
]);
export type FiscalRegime = z.infer<typeof fiscalRegimeSchema>;

export const cfdiUseSchema = z.enum(["G01", "G02", "G03", "D01", "S01", "CP01"]);
export type CfdiUse = z.infer<typeof cfdiUseSchema>;

export const invoiceRequestInputSchema = z.discriminatedUnion("requested", [
  z.object({ requested: z.literal(false) }),
  z.object({
    requested: z.literal(true),
    rfc: z.string().trim().toUpperCase().regex(/^[A-Z&Ñ]{3,4}[0-9]{6}[A-Z0-9]{3}$/),
    legalName: z.string().trim().min(3).max(300),
    fiscalPostalCode: z.string().trim().regex(/^\d{5}$/),
    fiscalRegime: fiscalRegimeSchema,
    cfdiUse: cfdiUseSchema,
    email: z.string().trim().email().max(254),
    observations: z.string().trim().max(1000).nullable(),
  }),
]);
export type InvoiceRequestInput = z.infer<typeof invoiceRequestInputSchema>;

export const counterSaleCreateSchema = z.object({
  operationType: counterSaleOperationSchema,
  items: z.array(z.object({
    sku: z.string().regex(/^\d{6}$/),
    quantity: z.number().int().min(1).max(999),
  })).min(1).max(100),
  payments: z.array(counterSalePaymentInputSchema).min(1).max(50),
  customer: counterSaleCustomerInputSchema.nullable(),
  invoice: invoiceRequestInputSchema.default({ requested: false }),
}).superRefine((value, context) => {
  const seen = new Set<string>();
  value.items.forEach((item, index) => {
    if (seen.has(item.sku)) {
      context.addIssue({ code: "custom", path: ["items", index, "sku"], message: "No repitas productos en el carrito." });
    }
    seen.add(item.sku);
  });
  if (value.operationType === "LAYAWAY_50" && !value.customer) {
    context.addIssue({ code: "custom", path: ["customer"], message: "Captura al cliente para realizar el apartado." });
  }
  if (value.operationType === "LIQUIDATED" && value.customer) {
    context.addIssue({ code: "custom", path: ["customer"], message: "La venta liquidada de mostrador no requiere cliente." });
  }
});
export type CounterSaleCreate = z.infer<typeof counterSaleCreateSchema>;

export const counterSaleSchema = z.object({
  id: identifierSchema,
  folio: z.string().min(1).max(80),
  branchId: identifierSchema,
  branchCode: z.string().min(1).max(40),
  branchName: z.string().min(1).max(160),
  operationType: counterSaleOperationSchema,
  status: z.enum(["PAID", "LAYAWAY"]),
  customerName: z.string().min(1).max(330),
  items: z.array(z.object({
    productId: identifierSchema,
    sku: z.string().regex(/^\d{6}$/),
    categoryCode: z.string().min(1).max(20),
    categoryLabel: z.string().min(1).max(20),
    description: z.string().min(1).max(500),
    brand: z.string().min(1).max(100),
    color: z.string().min(1).max(100),
    quantity: z.number().int().min(1),
    unitPrice: z.number().positive(),
    lineTotal: z.number().positive(),
    remainingPhysicalQuantity: z.number().int().min(0),
  })),
  payments: z.array(z.object({
    methodCode: counterPaymentMethodSchema,
    methodLabel: z.string().min(1).max(100),
    reference: z.string().nullable(),
    amount: z.number().positive(),
  })),
  listTotal: z.number().positive(),
  discount: z.number().min(0),
  subtotal: z.number().positive(),
  vat: z.number().min(0),
  total: z.number().positive(),
  paid: z.number().positive(),
  balance: z.number().min(0),
  inventoryDeducted: z.boolean(),
  layawayExpiresAt: z.string().datetime({ offset: true }).nullable(),
  createdByName: z.string().min(1).max(160),
  createdAt: z.string().datetime({ offset: true }),
});
export type CounterSale = z.infer<typeof counterSaleSchema>;
export const counterSaleResponseSchema = z.object({ data: counterSaleSchema });

export const opticalSaleCreateSchema = z.object({
  quoteId: identifierSchema,
  payments: z.array(protectedSalePaymentInputSchema).min(1).max(50),
  invoiceDecision: saleInvoiceDecisionSchema.default("NOT_APPLICABLE"),
  invoice: invoiceRequestInputSchema.default({ requested: false }),
}).superRefine((value, context) => {
  if (value.invoiceDecision === "YES" && !value.invoice.requested) {
    context.addIssue({ code: "custom", path: ["invoice"], message: "Captura los datos fiscales para solicitar factura." });
  }
  if (value.invoiceDecision !== "YES" && value.invoice.requested) {
    context.addIssue({ code: "custom", path: ["invoiceDecision"], message: "La decisión fiscal no corresponde a los datos capturados." });
  }
});
export type OpticalSaleCreate = z.infer<typeof opticalSaleCreateSchema>;

export const opticalSaleStatusSchema = z.enum(["PAID", "OPEN", "LAYAWAY"]);
export type OpticalSaleStatus = z.infer<typeof opticalSaleStatusSchema>;
export const opticalWorkStatusSchema = z.enum([
  "ON_LAYAWAY",
  "PENDING_SEND",
  "IN_LABORATORY",
  "PENDING_RESEND",
  "READY_FOR_DELIVERY",
  "DELIVERED",
  "CANCELLED",
]);
export type OpticalWorkStatus = z.infer<typeof opticalWorkStatusSchema>;

export const opticalSaleSchema = z.object({
  id: identifierSchema,
  folio: z.string().min(1).max(80),
  quoteId: identifierSchema,
  quoteFolio: z.string().min(1).max(80),
  branchId: identifierSchema,
  branchCode: z.string().min(1).max(40),
  branchName: z.string().min(1).max(160),
  status: opticalSaleStatusSchema,
  customer: customerSchema,
  frame: opticalQuoteSchema.shape.frame,
  lens: opticalQuoteSchema.shape.lens,
  prescription: opticalPrescriptionInputSchema,
  bevelType: z.string().min(1).max(100),
  bevelFinish: z.string().max(100).nullable(),
  estimatedDeliveryDate: isoDateSchema,
  framePrice: z.number().positive(),
  lensPrice: z.number().positive(),
  payments: z.array(z.object({
    methodCode: counterPaymentMethodSchema,
    methodLabel: z.string().min(1).max(100),
    reference: z.string().nullable(),
    amount: z.number().positive(),
  })).min(1),
  subtotal: z.number().positive(),
  vat: z.number().min(0),
  total: z.number().positive(),
  paid: z.number().positive(),
  balance: z.number().min(0),
  paidPercentage: z.number().min(0).max(100),
  inventoryDeducted: z.boolean(),
  remainingFrameQuantity: z.number().int().min(0).nullable(),
  laboratoryAuthorized: z.boolean(),
  workStatus: opticalWorkStatusSchema,
  laboratoryOrderId: identifierSchema,
  layawayExpiresAt: z.string().datetime({ offset: true }).nullable(),
  invoiceRequested: z.boolean(),
  createdByName: z.string().min(1).max(160),
  createdAt: z.string().datetime({ offset: true }),
});
export type OpticalSale = z.infer<typeof opticalSaleSchema>;
export const opticalSaleResponseSchema = z.object({ data: opticalSaleSchema });

export const invoiceRequestStatusSchema = z.enum(["PENDING_PAYMENT", "PENDING", "PACKAGE_READY", "SENT"]);
export type InvoiceRequestStatus = z.infer<typeof invoiceRequestStatusSchema>;

export const invoiceRequestSchema = z.object({
  id: identifierSchema,
  internalFolio: z.string().regex(/^\d{4,20}$/),
  saleId: identifierSchema,
  saleFolio: z.string().min(1).max(80),
  saleType: z.enum(["COUNTER", "OPTICAL"]),
  branchId: identifierSchema,
  branchCode: z.string().min(1).max(40),
  branchName: z.string().min(1).max(160),
  customerName: z.string().min(1).max(330),
  customerPhone: z.string().max(25).nullable(),
  rfc: z.string().min(12).max(13),
  legalName: z.string().min(3).max(300),
  fiscalPostalCode: z.string().regex(/^\d{5}$/),
  fiscalRegime: fiscalRegimeSchema,
  cfdiUse: cfdiUseSchema,
  paymentForm: counterPaymentMethodSchema,
  paymentMethod: z.enum(["PUE", "PPD"]),
  email: z.string().email(),
  observations: z.string().max(1000).nullable(),
  subtotal: z.number().positive(),
  vat: z.number().min(0),
  total: z.number().positive(),
  paid: z.number().min(0),
  balance: z.number().min(0),
  status: invoiceRequestStatusSchema,
  cfdiVersion: z.enum(["3.3", "4.0"]).nullable(),
  uuid: z.string().uuid().nullable(),
  packageAvailable: z.boolean(),
  requestedByName: z.string().min(1).max(160),
  requestedAt: z.string().datetime({ offset: true }),
  sentAt: z.string().datetime({ offset: true }).nullable(),
});
export type InvoiceRequest = z.infer<typeof invoiceRequestSchema>;
export const invoiceRequestsResponseSchema = z.object({ data: z.array(invoiceRequestSchema) });
export const invoiceRequestResponseSchema = z.object({ data: invoiceRequestSchema });

export const invoiceRequestCreateSchema = z.object({
  saleId: identifierSchema,
  invoice: invoiceRequestInputSchema.refine((value) => value.requested, "Captura los datos fiscales."),
});
export type InvoiceRequestCreate = z.infer<typeof invoiceRequestCreateSchema>;

export const fiscalPackageUploadResponseSchema = z.object({
  data: z.object({
    request: invoiceRequestSchema,
    packageName: z.string().min(5).max(240),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    message: z.string().min(1).max(500),
  }),
});

export const invoiceRecipientConfirmSchema = z.object({
  email: z.string().trim().email().max(254),
  branchCopy: z.boolean().default(false),
});
export type InvoiceRecipientConfirm = z.infer<typeof invoiceRecipientConfirmSchema>;

export const invoiceDeliveryResponseSchema = z.object({
  data: z.object({
    request: invoiceRequestSchema,
    deliveryId: identifierSchema,
    message: z.string().min(1).max(500),
  }),
});

export const laboratoryStatusSchema = z.enum(["ACTIVE", "INACTIVE"]);
export type LaboratoryStatus = z.infer<typeof laboratoryStatusSchema>;

export const laboratorySchema = z.object({
  id: identifierSchema,
  code: z.string().min(2).max(30),
  name: z.string().min(3).max(160),
  contactName: z.string().min(3).max(160).nullable(),
  phone: z.string().min(7).max(25).nullable(),
  status: laboratoryStatusSchema,
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});
export type Laboratory = z.infer<typeof laboratorySchema>;
export const laboratoriesResponseSchema = z.object({ data: z.array(laboratorySchema) });

export const laboratoryInputSchema = z.object({
  code: z.string().trim().min(2).max(30).regex(/^[A-Za-z0-9_-]+$/),
  name: z.string().trim().min(3).max(160),
  contactName: z.string().trim().min(3).max(160).nullable(),
  phone: z.string().trim().regex(/^[0-9+() -]{7,25}$/).nullable(),
});
export type LaboratoryInput = z.infer<typeof laboratoryInputSchema>;

export const laboratoryStatusInputSchema = z.object({ status: laboratoryStatusSchema });

export const workKindSchema = z.enum(["SALE", "WARRANTY_ADAPTATION", "WARRANTY_FACTORY"]);
export type WorkKind = z.infer<typeof workKindSchema>;

export const workSummarySchema = z.object({
  id: identifierSchema,
  saleId: identifierSchema,
  saleFolio: z.string().min(1).max(80),
  quoteFolio: z.string().min(1).max(80),
  customerName: z.string().min(1).max(330),
  phone: z.string().regex(/^\d{10}$/),
  branchId: identifierSchema,
  branchCode: z.string().min(1).max(40),
  branchName: z.string().min(1).max(160),
  workKind: workKindSchema,
  status: opticalWorkStatusSchema,
  cycleNumber: z.number().int().min(1).max(20),
  lensLabel: z.string().min(1).max(700),
  frameLabel: z.string().min(1).max(500),
  preferredSupplier: z.string().max(160).nullable(),
  alternateSuppliers: z.array(z.string().max(160)).max(5),
  laboratoryId: identifierSchema.nullable(),
  laboratoryName: z.string().max(160).nullable(),
  packageId: identifierSchema.nullable(),
  packageFolio: z.string().max(100).nullable(),
  total: z.number().positive(),
  subtotal: z.number().positive(),
  vat: z.number().min(0),
  discount: z.number().min(0),
  paid: z.number().min(0),
  balance: z.number().min(0),
  layawayExpiresAt: z.string().datetime({ offset: true }).nullable(),
  invoiceRequested: z.boolean(),
  createdByName: z.string().min(1).max(160),
  estimatedDeliveryDate: isoDateSchema,
  hasIncident: z.boolean(),
  createdAt: z.string().datetime({ offset: true }),
  sentAt: z.string().datetime({ offset: true }).nullable(),
  readyAt: z.string().datetime({ offset: true }).nullable(),
  deliveredAt: z.string().datetime({ offset: true }).nullable(),
});
export type WorkSummary = z.infer<typeof workSummarySchema>;
export const workListResponseSchema = z.object({ data: z.array(workSummarySchema) });

export const workHistoryItemSchema = z.object({
  eventType: z.string().min(1).max(100),
  fromStatus: opticalWorkStatusSchema.nullable(),
  toStatus: opticalWorkStatusSchema.nullable(),
  actorName: z.string().min(1).max(160),
  occurredAt: z.string().datetime({ offset: true }),
  metadata: z.record(z.string(), z.unknown()),
});

export const workPaymentHistoryItemSchema = z.object({
  sequence: z.number().int().min(1).max(50),
  methodCode: counterPaymentMethodSchema,
  methodLabel: z.string().min(1).max(100),
  reference: z.string().nullable(),
  amount: z.number().positive(),
  receivedAt: z.string().datetime({ offset: true }),
  accumulatedPaid: z.number().positive(),
  balanceAfter: z.number().min(0),
});
export type WorkPaymentHistoryItem = z.infer<typeof workPaymentHistoryItemSchema>;

export const workDocumentTypeSchema = z.enum([
  "FULL_PACKAGE",
  "CUSTOMER_TICKET",
  "OPTICAL_COPY",
  "LABORATORY_ORDER",
  "CUSTOMER_FRAME_RESPONSIVA",
  "PAYMENT_RECEIPT",
]);
export type WorkDocumentType = z.infer<typeof workDocumentTypeSchema>;

export const workDocumentPrintInputSchema = z.object({
  documentType: workDocumentTypeSchema,
  paymentSequence: z.number().int().min(1).max(50).nullable().default(null),
  output: z.enum(["LOCAL", "BROWSER"]),
}).superRefine((value, context) => {
  if (value.documentType === "PAYMENT_RECEIPT" && value.paymentSequence === null) {
    context.addIssue({
      code: "custom",
      path: ["paymentSequence"],
      message: "Identifica el abono que se reimprimió.",
    });
  }
  if (value.documentType !== "PAYMENT_RECEIPT" && value.paymentSequence !== null) {
    context.addIssue({
      code: "custom",
      path: ["paymentSequence"],
      message: "La secuencia de abono sólo aplica al comprobante de pago.",
    });
  }
});
export type WorkDocumentPrintInput = z.infer<typeof workDocumentPrintInputSchema>;

export const workDetailSchema = workSummarySchema.extend({
  orderSnapshot: z.record(z.string(), z.unknown()),
  saleSnapshot: opticalSaleSchema,
  history: z.array(workHistoryItemSchema),
  incidents: z.array(z.object({
    type: z.string().min(1).max(100),
    description: z.string().min(1).max(1000),
    createdByName: z.string().min(1).max(160),
    createdAt: z.string().datetime({ offset: true }),
  })),
  whatsappOpenCount: z.number().int().min(0),
  paymentHistory: z.array(workPaymentHistoryItemSchema),
  lastPayment: workPaymentHistoryItemSchema.nullable(),
});
export type WorkDetail = z.infer<typeof workDetailSchema>;
export const workDetailResponseSchema = z.object({ data: workDetailSchema });

export const cashDrawerOpeningTypeSchema = z.enum([
  "ADMIN_TEST",
  "COUNTER_SALE_CASH",
  "OPTICAL_SALE_CASH",
  "WORK_PAYMENT_CASH",
]);
export type CashDrawerOpeningType = z.infer<typeof cashDrawerOpeningTypeSchema>;

export const privateIpv4Schema = z.string().trim().refine((value) => {
  const octets = value.split(".").map(Number);
  if (octets.length !== 4 || octets.some((octet) => !Number.isInteger(octet) || octet < 0 || octet > 255)) {
    return false;
  }
  const [first = -1, second = -1] = octets;
  return first === 10
    || (first === 172 && second >= 16 && second <= 31)
    || (first === 192 && second === 168);
}, "Captura una dirección IPv4 privada válida.");

const cashDrawerHardwareInputShape = {
  terminalId: identifierSchema,
  terminalLabel: z.string().trim().min(2).max(100),
  printerAddress: privateIpv4Schema,
  deviceId: z.string().trim().regex(/^[A-Za-z0-9_-]{1,64}$/),
  drawerPin: z.union([z.literal(2), z.literal(5)]),
};

export const cashDrawerOpeningInputSchema = z.discriminatedUnion("openingType", [
  z.object({
    ...cashDrawerHardwareInputShape,
    openingType: z.literal("ADMIN_TEST"),
    operationReference: z.null(),
    paymentSequence: z.null(),
    reason: z.string().trim().min(10).max(500),
  }),
  z.object({
    ...cashDrawerHardwareInputShape,
    openingType: z.literal("COUNTER_SALE_CASH"),
    operationReference: z.string().trim().min(3).max(100),
    paymentSequence: z.null(),
    reason: z.string().trim().min(5).max(500),
  }),
  z.object({
    ...cashDrawerHardwareInputShape,
    openingType: z.literal("OPTICAL_SALE_CASH"),
    operationReference: z.string().trim().min(3).max(100),
    paymentSequence: z.null(),
    reason: z.string().trim().min(5).max(500),
  }),
  z.object({
    ...cashDrawerHardwareInputShape,
    openingType: z.literal("WORK_PAYMENT_CASH"),
    operationReference: z.string().trim().min(3).max(100),
    paymentSequence: z.number().int().min(1).max(50),
    reason: z.string().trim().min(5).max(500),
  }),
]);
export type CashDrawerOpeningInput = z.infer<typeof cashDrawerOpeningInputSchema>;

export const cashDrawerOpeningStatusSchema = z.enum(["REQUESTED", "OPENED", "FAILED"]);
export type CashDrawerOpeningStatus = z.infer<typeof cashDrawerOpeningStatusSchema>;

export const cashDrawerOpeningSchema = z.object({
  id: identifierSchema,
  branchId: identifierSchema,
  branchCode: z.string().min(1).max(40),
  branchName: z.string().min(1).max(160),
  actorUserId: identifierSchema,
  actorName: z.string().min(1).max(160),
  openingType: cashDrawerOpeningTypeSchema,
  operationReference: z.string().nullable(),
  paymentSequence: z.number().int().min(1).max(50).nullable(),
  reason: z.string().min(5).max(500),
  terminalId: identifierSchema,
  terminalLabel: z.string().min(2).max(100),
  printerAddress: privateIpv4Schema,
  deviceId: z.string().min(1).max(64),
  drawerPin: z.union([z.literal(2), z.literal(5)]),
  status: cashDrawerOpeningStatusSchema,
  errorCode: z.string().nullable(),
  errorMessage: z.string().nullable(),
  requestedAt: z.string().datetime({ offset: true }),
  completedAt: z.string().datetime({ offset: true }).nullable(),
});
export type CashDrawerOpening = z.infer<typeof cashDrawerOpeningSchema>;

export const cashDrawerOpeningResponseSchema = z.object({
  data: z.object({ opening: cashDrawerOpeningSchema, shouldOpen: z.boolean() }),
});
export const cashDrawerOpeningsResponseSchema = z.object({ data: z.array(cashDrawerOpeningSchema) });

export const cashDrawerOpeningResultInputSchema = z.discriminatedUnion("outcome", [
  z.object({ outcome: z.literal("OPENED"), errorCode: z.null(), errorMessage: z.null() }),
  z.object({
    outcome: z.literal("FAILED"),
    errorCode: z.string().trim().regex(/^[A-Z0-9_]{3,80}$/),
    errorMessage: z.string().trim().min(3).max(300),
  }),
]);
export type CashDrawerOpeningResultInput = z.infer<typeof cashDrawerOpeningResultInputSchema>;
export const cashDrawerOpeningResultResponseSchema = z.object({ data: cashDrawerOpeningSchema });

export const laboratoryPackageInputSchema = z.object({
  orderIds: z.array(identifierSchema).min(1).max(500),
  laboratoryId: identifierSchema,
  guideRequired: z.boolean(),
  guideNumber: z.string().trim().min(3).max(100).nullable(),
  secondaryReason: z.string().trim().min(10).max(500).nullable(),
}).superRefine((value, context) => {
  if (new Set(value.orderIds).size !== value.orderIds.length) {
    context.addIssue({ code: "custom", path: ["orderIds"], message: "No repitas trabajos dentro del mismo paquete." });
  }
  if (value.guideRequired && !value.guideNumber) {
    context.addIssue({ code: "custom", path: ["guideNumber"], message: "Captura el número de guía." });
  }
  if (!value.guideRequired && value.guideNumber) {
    context.addIssue({ code: "custom", path: ["guideNumber"], message: "La guía no corresponde a este envío." });
  }
});
export type LaboratoryPackageInput = z.infer<typeof laboratoryPackageInputSchema>;

export const laboratoryPackageSchema = z.object({
  id: identifierSchema,
  folio: z.string().min(8).max(100),
  laboratory: laboratorySchema,
  laboratoryKind: z.enum(["PREFERRED", "SECONDARY"]),
  secondaryReason: z.string().nullable(),
  guideRequired: z.boolean(),
  guideNumber: z.string().nullable(),
  orderCount: z.number().int().positive(),
  orderIds: z.array(identifierSchema).min(1),
  createdByName: z.string().min(1).max(160),
  createdAt: z.string().datetime({ offset: true }),
});
export type LaboratoryPackage = z.infer<typeof laboratoryPackageSchema>;
export const laboratoryPackageResponseSchema = z.object({ data: laboratoryPackageSchema });

export const workReviewInputSchema = z.object({
  result: z.enum(["SATISFACTORY", "NON_SATISFACTORY"]),
  decision: z.enum(["READY_FOR_DELIVERY", "REPROCESS"]),
  comments: z.string().trim().min(3).max(1000).nullable(),
  incidentType: z.string().trim().min(3).max(100).nullable(),
  incidentDescription: z.string().trim().min(10).max(1000).nullable(),
}).superRefine((value, context) => {
  if (value.result === "SATISFACTORY" && value.decision !== "READY_FOR_DELIVERY") {
    context.addIssue({ code: "custom", path: ["decision"], message: "Un trabajo satisfactorio debe quedar listo para entrega." });
  }
  if (value.result === "NON_SATISFACTORY" && (!value.incidentType || !value.incidentDescription)) {
    context.addIssue({ code: "custom", path: ["incidentDescription"], message: "Documenta la incidencia del trabajo." });
  }
});
export type WorkReviewInput = z.infer<typeof workReviewInputSchema>;

export const workPaymentInputSchema = z.object({
  payment: protectedSalePaymentInputSchema,
  invoiceDecision: saleInvoiceDecisionSchema.default("NOT_APPLICABLE"),
  invoice: invoiceRequestInputSchema.default({ requested: false }),
}).superRefine((value, context) => {
  if (value.invoiceDecision === "YES" && !value.invoice.requested) {
    context.addIssue({ code: "custom", path: ["invoice"], message: "Captura los datos fiscales para solicitar factura." });
  }
  if (value.invoiceDecision !== "YES" && value.invoice.requested) {
    context.addIssue({ code: "custom", path: ["invoiceDecision"], message: "La decisión fiscal no corresponde a los datos capturados." });
  }
});
export type WorkPaymentInput = z.infer<typeof workPaymentInputSchema>;

export const workWarrantyInputSchema = z.object({
  warrantyType: z.enum(["ADAPTATION", "FACTORY_DEFECT"]),
  reason: z.string().trim().min(10).max(1000),
  prescription: opticalPrescriptionInputSchema.nullable(),
}).superRefine((value, context) => {
  if (value.warrantyType === "ADAPTATION" && !value.prescription) {
    context.addIssue({ code: "custom", path: ["prescription"], message: "La garantía de adaptación requiere la receta corregida." });
  }
  if (value.warrantyType === "FACTORY_DEFECT" && value.prescription) {
    context.addIssue({ code: "custom", path: ["prescription"], message: "El defecto de fábrica no modifica la receta." });
  }
});
export type WorkWarrantyInput = z.infer<typeof workWarrantyInputSchema>;

export const whatsappOpenResponseSchema = z.object({
  data: z.object({ url: z.string().url(), phone: z.string(), message: z.string() }),
});

export const clinicalReferenceTypeSchema = z.enum(["SALE", "QUOTE"]);
export type ClinicalReferenceType = z.infer<typeof clinicalReferenceTypeSchema>;

export const clinicalDocumentTypeSchema = z.enum([
  "CLINICAL_HISTORY",
  "AUTOREFRACTOR_TICKET",
  "LENSOMETER_TICKET",
]);
export type ClinicalDocumentType = z.infer<typeof clinicalDocumentTypeSchema>;

export const clinicalDocumentSchema = z.object({
  id: identifierSchema,
  type: clinicalDocumentTypeSchema,
  originalName: z.string().min(1).max(240),
  mimeType: z.enum(["application/pdf", "image/jpeg", "image/png"]),
  sizeBytes: z.number().int().positive().max(10 * 1024 * 1024),
  version: z.number().int().positive(),
  uploadedByName: z.string().min(1).max(160),
  uploadedAt: z.string().datetime({ offset: true }),
});
export type ClinicalDocument = z.infer<typeof clinicalDocumentSchema>;

export const clinicalOperationSchema = z.object({
  referenceType: clinicalReferenceTypeSchema,
  referenceId: identifierSchema,
  referenceNumber: z.string().min(1).max(80),
  customerName: z.string().min(1).max(330),
  branchId: identifierSchema,
  branchCode: z.string().min(1).max(40),
  branchName: z.string().min(1).max(160),
  operationDate: isoDateSchema,
  loadNumber: z.string().max(40).nullable(),
  status: z.enum(["PENDING", "COMPLETE"]),
  missingDocuments: z.array(clinicalDocumentTypeSchema),
  documents: z.array(clinicalDocumentSchema),
});
export type ClinicalOperation = z.infer<typeof clinicalOperationSchema>;

export const clinicalClosureSchema = z.object({
  period: z.string().regex(/^\d{4}-\d{2}$/),
  periodLabel: z.string().min(1).max(80),
  state: z.enum(["PENDING", "OVERDUE", "CLOSED"]),
  closed: z.boolean(),
  captureBlocked: z.boolean(),
  canClose: z.boolean(),
  withinWindow: z.boolean(),
  deadline: isoDateSchema,
  fileCount: z.number().int().min(0),
  originalBytes: z.number().int().min(0),
  closedAt: z.string().datetime({ offset: true }).nullable(),
  closedByName: z.string().max(160).nullable(),
  message: z.string().min(1).max(500),
});
export type ClinicalClosure = z.infer<typeof clinicalClosureSchema>;

export const clinicalPanelResponseSchema = z.object({
  data: z.object({
    date: isoDateSchema,
    operations: z.array(clinicalOperationSchema),
    totals: z.object({
      operations: z.number().int().min(0),
      complete: z.number().int().min(0),
      pending: z.number().int().min(0),
    }),
    closure: clinicalClosureSchema,
  }),
});

export const clinicalDetailResponseSchema = z.object({
  data: clinicalOperationSchema.extend({
    mode: z.enum(["UPLOAD", "CONTINUE", "MODIFY"]),
  }),
});

export const clinicalRecordsResponseSchema = z.object({
  data: z.object({
    month: z.string().regex(/^\d{4}-\d{2}$/),
    operations: z.array(clinicalOperationSchema),
    totalFiles: z.number().int().min(0),
    totalBytes: z.number().int().min(0),
  }),
});

export const clinicalUploadResponseSchema = z.object({
  data: z.object({
    loadNumber: z.string().min(1).max(40),
    complete: z.boolean(),
    savedDocuments: z.array(z.object({
      id: identifierSchema,
      type: clinicalDocumentTypeSchema,
      version: z.number().int().positive(),
    })).min(1).max(3),
    message: z.string().min(1).max(500),
  }),
});

export const clinicalClosureConfirmSchema = z.object({
  attemptId: identifierSchema,
  localPcBackupConfirmed: z.literal(true),
  externalDriveBackupConfirmed: z.literal(true),
});
export type ClinicalClosureConfirm = z.infer<typeof clinicalClosureConfirmSchema>;

export const clinicalClosureConfirmResponseSchema = z.object({
  data: z.object({ closure: clinicalClosureSchema, message: z.string().min(1).max(500) }),
});

export const kpiPeriodSchema = z.enum(["DAILY", "WEEKLY", "MONTHLY", "ANNUAL"]);
export type KpiPeriod = z.infer<typeof kpiPeriodSchema>;

export const kpiTrafficInputSchema = z.object({
  date: isoDateSchema,
  quantity: z.number().int().min(0).max(100_000),
  replacementReason: z.string().trim().min(10).max(500).nullable().default(null),
});
export type KpiTrafficInput = z.infer<typeof kpiTrafficInputSchema>;

export const kpiGoalPlanInputSchema = z.object({
  month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
  monthlyNetSales: z.number().finite().min(95_000).max(100_000_000),
  replacementReason: z.string().trim().min(10).max(500).nullable().default(null),
});
export type KpiGoalPlanInput = z.infer<typeof kpiGoalPlanInputSchema>;

export const kpiMetricUnitSchema = z.enum(["COUNT", "PERCENT", "CURRENCY"]);
export type KpiMetricUnit = z.infer<typeof kpiMetricUnitSchema>;

export const kpiMetricSchema = z.object({
  code: z.string().min(1).max(80),
  label: z.string().min(1).max(160),
  section: z.string().min(1).max(160),
  value: z.number().finite(),
  volume: z.number().finite().nullable(),
  unit: kpiMetricUnitSchema,
  goal: z.number().finite().nullable(),
  goalDirection: z.enum(["MIN", "MAX"]).nullable(),
  progressPercent: z.number().finite().nullable(),
  status: z.enum(["NO_GOAL", "ON_TRACK", "OFF_TRACK"]),
});
export type KpiMetric = z.infer<typeof kpiMetricSchema>;

export const kpiComparisonSchema = z.object({
  code: z.enum(["PREVIOUS", "PREVIOUS_MONTH", "PREVIOUS_YEAR"]),
  label: z.string().min(1).max(120),
  startDate: isoDateSchema,
  endDate: isoDateSchema,
  netSales: z.number().min(0),
  firstPairs: z.number().int().min(0),
  netConversion: z.number().min(0),
  multifocalConversion: z.number().min(0),
});
export type KpiComparison = z.infer<typeof kpiComparisonSchema>;

export const kpiGoalPlanSchema = z.object({
  id: identifierSchema,
  month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
  monthlyNetSales: z.number().min(95_000),
  ruleVersion: z.string().min(1).max(80),
  createdByName: z.string().min(1).max(160),
  createdAt: z.string().datetime({ offset: true }),
  replacementReason: z.string().nullable(),
});
export type KpiGoalPlan = z.infer<typeof kpiGoalPlanSchema>;

export const kpiReportSchema = z.object({
  period: kpiPeriodSchema,
  selectedDate: isoDateSchema,
  startDate: isoDateSchema,
  endDate: isoDateSchema,
  branchId: identifierSchema,
  branchName: z.string().min(1).max(160),
  generatedAt: z.string().datetime({ offset: true }),
  metrics: z.array(kpiMetricSchema),
  comparisons: z.array(kpiComparisonSchema),
  traffic: z.object({
    capturedDays: z.number().int().min(0),
    expectedDays: z.number().int().min(1),
    missingDates: z.array(isoDateSchema),
  }),
  goalPlan: kpiGoalPlanSchema.nullable(),
});
export type KpiReport = z.infer<typeof kpiReportSchema>;

export const kpiReportResponseSchema = z.object({ data: kpiReportSchema });
export const kpiGoalPreviewResponseSchema = z.object({
  data: z.object({
    month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
    monthlyNetSales: z.number().min(95_000),
    targets: z.record(z.string(), z.number().finite()),
  }),
});
export const kpiGoalPlanResponseSchema = z.object({ data: kpiGoalPlanSchema });
export const kpiTrafficResponseSchema = z.object({
  data: z.object({
    date: isoDateSchema,
    quantity: z.number().int().min(0).max(100_000),
    replaced: z.boolean(),
  }),
});

export const cancellationOperationalTypeSchema = z.enum(["ADMINISTRATIVE", "WARRANTY"]);
export type CancellationOperationalType = z.infer<typeof cancellationOperationalTypeSchema>;

export const cancellationFinancialDestinationSchema = z.enum(["REFUND", "TRANSFER_NEW_ORDER", "NO_AMOUNT"]);
export type CancellationFinancialDestination = z.infer<typeof cancellationFinancialDestinationSchema>;

export const cancellationStatusSchema = z.enum(["PENDING", "APPROVED", "REJECTED", "EXPIRED", "EXECUTED"]);
export type CancellationStatus = z.infer<typeof cancellationStatusSchema>;

export const cancellableSaleSchema = z.object({
  id: identifierSchema,
  folio: z.string().min(1).max(80),
  branchId: identifierSchema,
  branchCode: z.string().min(1).max(40),
  branchName: z.string().min(1).max(160),
  customerName: z.string().min(1).max(330),
  status: z.enum(["PAID", "OPEN", "LAYAWAY"]),
  total: z.number().positive(),
  paid: z.number().min(0),
  balance: z.number().min(0),
  sentToLaboratory: z.boolean(),
  laboratoryStatus: z.string().max(60),
  frameOrigin: z.enum(["CATALOG", "CUSTOMER"]),
  frameSku: z.string().regex(/^\d{6}$/).nullable(),
  frameDescription: z.string().min(1).max(500),
  frameReturnRequired: z.boolean(),
  invoiceRequested: z.boolean(),
  createdAt: z.string().datetime({ offset: true }),
});
export type CancellableSale = z.infer<typeof cancellableSaleSchema>;
export const cancellableSaleResponseSchema = z.object({ data: cancellableSaleSchema });

export const cancellationRequestInputSchema = z.object({
  saleFolio: z.string().trim().min(4).max(80),
  operationalType: cancellationOperationalTypeSchema,
  financialDestination: cancellationFinancialDestinationSchema,
  reason: z.string().trim().min(10).max(1000),
}).superRefine((value, context) => {
  if (value.financialDestination === "NO_AMOUNT") {
    context.addIssue({ code: "custom", path: ["financialDestination"], message: "El destino sin importe se determina automáticamente." });
  }
});
export type CancellationRequestInput = z.infer<typeof cancellationRequestInputSchema>;

export const cancellationDecisionInputSchema = z.object({
  decision: z.enum(["APPROVE", "REJECT"]),
  comment: z.string().trim().min(8).max(500),
});
export type CancellationDecisionInput = z.infer<typeof cancellationDecisionInputSchema>;

export const cancellationExecuteInputSchema = z.object({
  authorizationCode: z.string().trim().toUpperCase().regex(/^[A-Z2-9]{8}$/),
  frameReceived: z.boolean(),
});
export type CancellationExecuteInput = z.infer<typeof cancellationExecuteInputSchema>;

export const cancellationPrintInputSchema = z.object({
  documentType: z.enum(["CANCELLATION_TICKET", "CREDIT_NOTE"]),
  copies: z.number().int().min(1).max(5),
});
export type CancellationPrintInput = z.infer<typeof cancellationPrintInputSchema>;

export const creditNoteApplyInputSchema = z.object({
  redemptionCode: z.string().trim().toUpperCase().regex(/^[A-Z2-9]{10}$/),
  destinationSaleFolio: z.string().trim().min(4).max(80),
  amount: z.number().finite().positive().max(99_999_999.99),
});
export type CreditNoteApplyInput = z.infer<typeof creditNoteApplyInputSchema>;

export const creditNoteRefundInputSchema = z.object({
  externalReference: z.string().trim().min(3).max(160),
});
export type CreditNoteRefundInput = z.infer<typeof creditNoteRefundInputSchema>;

export const creditNoteSchema = z.object({
  id: identifierSchema,
  folio: z.string().min(1).max(80),
  status: z.enum(["ACTIVE", "PARTIALLY_APPLIED", "APPLIED", "REFUND_PENDING", "REFUNDED", "CANCELLED"]),
  destination: z.enum(["REFUND", "TRANSFER_NEW_ORDER"]),
  originalAmount: z.number().positive(),
  balance: z.number().min(0),
  createdAt: z.string().datetime({ offset: true }),
  redemptionCode: z.string().nullable().optional(),
});
export type CreditNote = z.infer<typeof creditNoteSchema>;

export const cancellationRequestSchema = z.object({
  id: identifierSchema,
  folio: z.string().min(1).max(80),
  saleId: identifierSchema,
  saleFolio: z.string().min(1).max(80),
  branchId: identifierSchema,
  branchCode: z.string().min(1).max(40),
  branchName: z.string().min(1).max(160),
  customerName: z.string().min(1).max(330),
  status: cancellationStatusSchema,
  scope: z.enum(["SALE_WITH_BALANCE", "PAID_SALE"]),
  operationalType: cancellationOperationalTypeSchema,
  financialDestination: cancellationFinancialDestinationSchema,
  reason: z.string().min(10).max(1000),
  authorizedAmount: z.number().min(0),
  sentToLaboratory: z.boolean(),
  lensWaste: z.boolean(),
  frameSku: z.string().regex(/^\d{6}$/).nullable(),
  frameReturnRequired: z.boolean(),
  fiscalState: z.enum(["NO_INVOICE", "FISCAL_REVIEW_REQUIRED"]),
  requestedBy: z.string().min(1).max(160),
  requestedByUserId: identifierSchema.optional(),
  requestedAt: z.string().datetime({ offset: true }),
  decidedBy: z.string().min(1).max(160).nullable(),
  decidedAt: z.string().datetime({ offset: true }).nullable(),
  decisionComment: z.string().max(500).nullable(),
  authorizationExpiresAt: z.string().datetime({ offset: true }).nullable(),
  executedBy: z.string().min(1).max(160).nullable(),
  executedAt: z.string().datetime({ offset: true }).nullable(),
  creditNote: creditNoteSchema.nullable(),
});
export type CancellationRequest = z.infer<typeof cancellationRequestSchema>;
export const cancellationRequestsResponseSchema = z.object({ data: z.array(cancellationRequestSchema) });
export const cancellationRequestResponseSchema = z.object({ data: cancellationRequestSchema });
export const cancellationDecisionResponseSchema = z.object({
  data: z.object({ request: cancellationRequestSchema, authorizationCode: z.string().nullable() }),
});
export const cancellationExecutionResponseSchema = z.object({
  data: z.object({ request: cancellationRequestSchema, creditNote: creditNoteSchema.nullable() }),
});

export const notificationSchema = z.object({
  key: z.string().min(3).max(240),
  kind: z.enum(["CANCELLATION", "DAILY_CLOSE", "REMITTANCE", "INVOICE", "CLINICAL_CLOSE"]),
  title: z.string().min(1).max(160),
  detail: z.string().min(1).max(500),
  count: z.number().int().min(1),
  read: z.boolean(),
  action: z.enum(["OPEN_CANCELLATIONS", "INFORMATION_ONLY"]),
});
export type Notification = z.infer<typeof notificationSchema>;
export const notificationCenterResponseSchema = z.object({
  data: z.object({ unreadCount: z.number().int().min(0), notifications: z.array(notificationSchema) }),
});
export const notificationReadInputSchema = z.object({ key: z.string().trim().min(3).max(240) });

export const dailyClosureInputSchema = z.object({ businessDate: isoDateSchema });
export const dailyClosureResponseSchema = z.object({
  data: z.object({
    id: identifierSchema,
    businessDate: isoDateSchema,
    saleCount: z.number().int().min(0),
    grossSales: z.number().min(0),
    cancelledAmount: z.number().min(0),
    collectedAmount: z.number().min(0),
    closedAt: z.string().datetime({ offset: true }),
  }),
});

export const healthResponseSchema = z.object({
  service: z.literal("cosmo-saas-api"),
  status: z.enum(["ok", "degraded"]),
  environment: z.string(),
  release: z.string(),
  requestId: z.string().uuid(),
  timestamp: z.string().datetime({ offset: true }),
});
export type HealthResponse = z.infer<typeof healthResponseSchema>;

export const apiErrorSchema = z.object({
  error: z.object({
    code: z.string().min(1),
    message: z.string().min(1),
    requestId: z.string().uuid(),
  }),
});
export type ApiError = z.infer<typeof apiErrorSchema>;

export const sessionResponseSchema = z.object({
  data: sessionIdentitySchema,
});
export type SessionResponse = z.infer<typeof sessionResponseSchema>;
